import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BarChart3 } from 'lucide-react'
import ThemeNavigation from './ThemeNavigation'
import NavigationButtons from './NavigationButtons'
import QuestionCard from './QuestionCard'
import RadarChart from './RadarChart'
import { useAssessmentProgress } from '../hooks/useLocalStorage'
import { themesToDomains, calculateThemeScores } from '../utils/themeUtils'
import { useQuestionnaire } from '../hooks/useQuestionnaire'

function ThemeAssessment({ onComplete, onBack, clientData }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const { 
    assessmentScores, 
    saveAssessmentScores, 
    currentThemeIndex: savedThemeIndex,
    saveCurrentThemeIndex 
  } = useAssessmentProgress()
  
  // Get theme index from URL, fallback to saved or 0
  // With comprehensive validation
  const getInitialThemeIndex = () => {
    const urlTheme = searchParams.get('theme')
    
    // Try URL parameter first
    if (urlTheme !== null) {
      const themeIndex = parseInt(urlTheme, 10)
      if (!isNaN(themeIndex) && isFinite(themeIndex) && themeIndex >= 0) {
        return themeIndex
      }
      console.warn('⚠️ Invalid theme parameter in URL, falling back to saved theme')
    }
    
    // Fallback to saved theme index from localStorage
    if (typeof savedThemeIndex === 'number' && savedThemeIndex >= 0) {
      return savedThemeIndex
    }
    
    // Ultimate fallback
    return 0
  }
  
  const [currentThemeIndex, setCurrentThemeIndex] = useState(getInitialThemeIndex())
  const themeQuestionsRef = useRef(null)
  const questionRefs = useRef({})
  const autoScrolledQuestions = useRef(new Set()) // Track which questions have been auto-scrolled
  
  // Load questionnaire from API with fallback to local JSON
  const { questionnaire, loading: questionnaireLoading } = useQuestionnaire()
  
  // Initialize scores - using question IDs as keys instead of domain indices
  const [scores, setScores] = useState({})
  const [scoresInitialized, setScoresInitialized] = useState(false)
  
  // Initialize scores when questionnaire loads
  useEffect(() => {
    if (!questionnaire || !questionnaire.themes || scoresInitialized) return
    
    // Check if we have saved scores from localStorage
    if (assessmentScores && typeof assessmentScores === 'object' && Object.keys(assessmentScores).length > 0) {
      setScores(assessmentScores)
      setScoresInitialized(true)
      return
    }
    
    // Otherwise, initialize empty scores structure
    const initialScores = {}
    questionnaire.themes.forEach(theme => {
      if (theme.questions && Array.isArray(theme.questions)) {
        theme.questions.forEach(question => {
          initialScores[question.id] = null
        })
      }
    })
    setScores(initialScores)
    setScoresInitialized(true)
  }, [questionnaire, assessmentScores, scoresInitialized])
  
  // Auto-save scores to local storage whenever they change
  // IMPORTANT: This useEffect must be called BEFORE any conditional returns (Rules of Hooks)
  useEffect(() => {
    if (scoresInitialized && Object.keys(scores).length > 0) {
      saveAssessmentScores(scores)
    }
  }, [scores, saveAssessmentScores, scoresInitialized])
  
  // Sync theme index when URL changes (e.g., browser back/forward)
  // Also ensures URL always has valid theme parameter
  // IMPORTANT: Must be BEFORE conditional return (Rules of Hooks)
  useEffect(() => {
    const urlTheme = searchParams.get('theme')
    
    if (urlTheme !== null) {
      const themeIndex = parseInt(urlTheme, 10)
      
      // Validate URL theme parameter
      if (!isNaN(themeIndex) && isFinite(themeIndex) && themeIndex >= 0) {
        // Check if theme index is within bounds (wait for questionnaire to load)
        if (questionnaire?.themes && themeIndex >= questionnaire.themes.length) {
          console.warn(`⚠️ Theme index ${themeIndex} out of bounds (max: ${questionnaire.themes.length - 1}), resetting to 0`)
          navigate(`/assessment?theme=0`, { replace: true })
          return
        }
        
        // Update state if theme changed
        if (themeIndex !== currentThemeIndex) {
          setCurrentThemeIndex(themeIndex)
          saveCurrentThemeIndex(themeIndex)
          
          // Scroll to top when navigating via browser buttons
          if (themeQuestionsRef.current) {
            themeQuestionsRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        }
      } else {
        // Invalid theme in URL, reset to saved or 0
        console.warn('⚠️ Invalid theme parameter in URL, resetting to valid theme')
        const validTheme = (typeof savedThemeIndex === 'number' && savedThemeIndex >= 0) ? savedThemeIndex : 0
        navigate(`/assessment?theme=${validTheme}`, { replace: true })
      }
    } else {
      // No theme in URL - add it (handles old bookmarks or direct navigation to /assessment)
      console.log('📍 No theme parameter in URL, adding current theme')
      const validTheme = (typeof currentThemeIndex === 'number' && currentThemeIndex >= 0) ? currentThemeIndex : 0
      navigate(`/assessment?theme=${validTheme}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, questionnaire]) // Depend on searchParams and questionnaire
  
  // Return loading state if questionnaire is not yet loaded
  // IMPORTANT: All hooks must be called BEFORE this conditional return
  if (questionnaireLoading || !questionnaire || !questionnaire.themes) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du questionnaire...</p>
        </div>
      </div>
    )
  }
  
  const themes = questionnaire.themes

  const handleScoreChange = (questionId, value) => {
    setScores(prev => ({
      ...prev,
      [questionId]: parseInt(value)
    }))
  }

  const handleSliderRelease = (currentQuestionId) => {
    // Only auto-scroll if this question hasn't been auto-scrolled yet in this theme
    const scrollKey = `${currentThemeIndex}-${currentQuestionId}`
    if (autoScrolledQuestions.current.has(scrollKey)) {
      return // Already scrolled for this question in this theme, skip auto-scroll
    }
    
    // Mark this question as auto-scrolled
    autoScrolledQuestions.current.add(scrollKey)
    
    // Find the next unanswered question in the current theme only
    const currentThemeQuestions = currentTheme.questions
    const currentQuestionIndex = currentThemeQuestions.findIndex(q => q.id === currentQuestionId)
    
    // Look for the next unanswered question in the current theme
    for (let i = currentQuestionIndex + 1; i < currentThemeQuestions.length; i++) {
      const nextQuestion = currentThemeQuestions[i]
      if (scores[nextQuestion.id] === null || scores[nextQuestion.id] === undefined) {
        // Found an unanswered question - scroll to it
        const questionElement = questionRefs.current[nextQuestion.id]
        if (questionElement) {
          questionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
        return
      }
    }
    
    // If all questions are answered, don't auto-switch themes
    // Let the user review and manually click "Thème suivant"
  }

  const scrollToFirstUnansweredQuestion = () => {
    // Find the first unanswered question in the current theme
    const currentThemeQuestions = currentTheme.questions
    
    for (let i = 0; i < currentThemeQuestions.length; i++) {
      const question = currentThemeQuestions[i]
      if (scores[question.id] === null || scores[question.id] === undefined) {
        // Found an unanswered question - scroll to it
        const questionElement = questionRefs.current[question.id]
        if (questionElement) {
          questionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
        return
      }
    }
  }

  const handleThemeChange = (themeIndex) => {
    setCurrentThemeIndex(themeIndex)
    saveCurrentThemeIndex(themeIndex) // Save to localStorage
    
    // Update URL with new theme index (this creates browser history entry)
    navigate(`/assessment?theme=${themeIndex}`, { replace: false })
    
    // Smooth scroll to the top of the theme questions section
    if (themeQuestionsRef.current) {
      themeQuestionsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const handleComplete = () => {
    // Calculate theme scores and overall metrics
    const themeScores = calculateThemeScores(themes, scores)

    const completedScores = Object.values(scores).filter(score => score !== null && score !== undefined)
    const averageScore = completedScores.length > 0 
      ? completedScores.reduce((sum, score) => sum + score, 0) / completedScores.length 
      : 0

    const assessmentData = {
      scores,
      themeScores,
      themes,
      questionnaire,
      completedAt: new Date(),
      averageScore,
      totalQuestions: themes.reduce((total, theme) => total + theme.questions.length, 0),
      completedQuestions: completedScores.length
    }
    
    onComplete(assessmentData)
  }

  const currentTheme = themes[currentThemeIndex]
  const currentThemeQuestions = currentTheme.questions

  // Calculate statistics
  const totalQuestions = themes.reduce((total, theme) => total + theme.questions.length, 0)
  const completedQuestions = Object.values(scores).filter(score => score !== null && score !== undefined).length
  const completionRate = Math.round((completedQuestions / totalQuestions) * 100)
  
  const completedValues = Object.values(scores).filter(score => score !== null && score !== undefined)
  const averageScore = completedValues.length > 0 
    ? completedValues.reduce((sum, score) => sum + score, 0) / completedValues.length 
    : 0

  // Calculate theme-based data for radar chart
  const radarData = calculateThemeScores(themes, scores)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      {/* Main Content */}
      <div className="lg:col-span-3">
        {/* Theme Navigation */}
        <ThemeNavigation
          themes={themes}
          currentThemeIndex={currentThemeIndex}
          scores={scores}
          onThemeChange={handleThemeChange}
          onComplete={handleComplete}
          onBack={onBack}
          onScrollToFirstUnanswered={scrollToFirstUnansweredQuestion}
        />

        {/* Current Theme Questions */}
        <div className="space-y-6" ref={themeQuestionsRef}>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-cedec-blue flex items-center gap-3">
                <span className="text-3xl">{currentTheme.icon}</span>
                {currentTheme.title}
              </h2>
              <div className="text-sm text-gray-500">
                Client: {clientData?.firstName} {clientData?.lastName}
              </div>
            </div>

            <div className="space-y-6">
              {currentThemeQuestions.map((question, index) => (
                <div 
                  key={question.id}
                  ref={(el) => {
                    if (el) {
                      questionRefs.current[question.id] = el
                    }
                  }}
                >
                  <QuestionCard
                    question={question}
                    value={scores[question.id]}
                    onChange={(value) => handleScoreChange(question.id, value)}
                    questionIndex={index}
                    totalQuestions={currentThemeQuestions.length}
                    onSliderRelease={() => handleSliderRelease(question.id)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  Thème {currentThemeIndex + 1} sur {themes.length}: <span className="font-medium text-gray-800">{currentTheme.title}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {currentThemeQuestions.map(q => scores[q.id]).filter(s => s !== null && s !== undefined).length}/{currentThemeQuestions.length} questions complétées
                </div>
              </div>
              
              <NavigationButtons
                themes={themes}
                currentThemeIndex={currentThemeIndex}
                scores={scores}
                onThemeChange={handleThemeChange}
                onComplete={handleComplete}
                onBack={onBack}
                onScrollToFirstUnanswered={scrollToFirstUnansweredQuestion}
                size="default"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar - Analysis Panel */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
          <h3 className="text-xl font-semibold text-cedec-blue mb-6 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Analyse en temps réel
          </h3>

          {/* Radar Chart */}
          <div className="mb-6">
            <RadarChart 
              domains={themesToDomains(themes, 10)} // Max 10 chars for radar chart labels
              scores={radarData}
            />
          </div>

          {/* Overall Progress */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-cedec-blue">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700">Progression globale</span>
              <span className="text-sm font-bold text-cedec-blue">{completedQuestions}/{totalQuestions}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div 
                className="bg-gradient-to-r from-cedec-blue to-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${completionRate}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-600 text-center">
              {completionRate}% terminé
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div className={`p-4 rounded-lg text-center border-l-4 transition-all ${
              completedQuestions > 0 
                ? 'bg-white border-cedec-blue shadow-sm' 
                : 'bg-gray-50 border-gray-300'
            }`}>
              <div className={`text-2xl font-bold ${
                completedQuestions > 0 ? 'text-cedec-blue' : 'text-gray-400'
              }`}>
                {completedQuestions > 0 ? averageScore.toFixed(1) : '--'}
              </div>
              <div className="text-sm text-gray-600">Score moyen</div>
            </div>
            
            <div className={`p-4 rounded-lg text-center border-l-4 transition-all ${
              completedQuestions > 0 
                ? 'bg-white border-cedec-blue shadow-sm' 
                : 'bg-gray-50 border-gray-300'
            }`}>
              <div className={`text-2xl font-bold ${
                completedQuestions > 0 ? 'text-cedec-blue' : 'text-gray-400'
              }`}>
                {completedQuestions > 0 ? radarData.filter(score => score >= 7).length : '--'}
              </div>
              <div className="text-sm text-gray-600">Thèmes forts</div>
            </div>
          </div>

          {/* Scale Reference */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Échelle d'évaluation</h4>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span>0-2: Très faible / Inexistant</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded"></div>
                <span>3-4: Faible / Basique</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                <span>5-6: Moyen / Acceptable</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>7-8: Bon / Bien développé</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-700 rounded"></div>
                <span>9-10: Excellent / Exemplaire</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThemeAssessment
